import { WITHHOLD_PERIOD_LABELS } from 'constants/gcConstants';
import {
  MergedCrop,
  UnitCategory,
  UnitParameterType,
} from 'interfaces/IGlobalCatalogue';
import { ICrop, IUnit } from 'interfaces/IInput';
import {
  DEFAULT_POISON_SCHEDULE,
  IActiveConstituent,
  IGcProductGroup,
  IInputStep,
  IInputStepBase,
  IInputStepManufacturerSupplierFields,
  IInputStepProduction,
  IInputStepProductionCropFields,
  InputStepType,
  PoisonSchedule,
  SeedDressRateMode,
} from 'interfaces/IInputStep';
import { IInputTask } from 'interfaces/IInputTask';
import { PartialUserProfile } from 'interfaces/IUserProfile';
import { pickSeedSpeciesProductionFields } from 'utils/catalogueMergeUtils';
import { GUnitFilter } from 'utils/GUnitFilters';
import { getUserDisplayName } from 'utils/UserUtils';
import { v4 as uuidv4 } from 'uuid';

import { matchesByIdOrName, StepSelectorOption } from './stepConfig';

export interface StepSelection extends Omit<StepSelectorOption, 'label'> {
  stepType: InputStepType;
}

/** Drops the display-only label and tags the picker option with its group's step type. */
export const toStepSelection = (
  option: StepSelectorOption,
  stepType: InputStepType
): StepSelection => {
  // eslint-disable-next-line @typescript-eslint/no-unused-vars
  const { label, ...selectionFields } = option;
  return { ...selectionFields, stepType };
};

const buildStepBase = (
  task: IInputTask,
  selection: StepSelection,
  userProfile?: PartialUserProfile
): IInputStepBase => {
  const now = new Date().toISOString();
  const userName = getUserDisplayName(
    userProfile?.firstName,
    userProfile?.lastName
  );

  return {
    dateCreated: now,
    creatorId: userProfile?.id ?? null,
    snapCreator: userName || null,
    dateModified: now,
    modifierId: userProfile?.id ?? null,
    snapModifier: userName || null,
    startDate: null,
    endDate: null,
    area: task.area ?? null,
    areaUnit: task.areaUnit ?? null,
    timing: null,
    notes: null,
    stepId: selection.stepId ?? uuidv4(),
    stepType: selection.stepType,
    item: selection.item,
    globalId: selection.globalId ?? null,
  };
};

const buildRateCostFields = (selection: StepSelection) => ({
  rate: selection.rate ?? null,
  appUnit: selection.unit ?? null,
  cost: selection.cost ?? null,
});

const buildManufacturerSupplierFields = (
  source: Partial<IInputStepManufacturerSupplierFields> | null
): IInputStepManufacturerSupplierFields => ({
  manufacturerId: source?.manufacturerId ?? null,
  snapManufacturer: source?.snapManufacturer ?? null,
  supplierId: source?.supplierId ?? null,
  snapSupplier: source?.snapSupplier ?? null,
});

const DEFAULT_CO2E_FIELDS: Readonly<{
  co2eScope1: null;
  co2eScope2: null;
  co2eScope3: null;
  co2eUnit: null;
}> = {
  co2eScope1: null,
  co2eScope2: null,
  co2eScope3: null,
  co2eUnit: null,
};

const SOIL_INPUT_SMALL_LIQUID_UNIT_IDS = new Set<string>([
  '8c88de9b-6d89-4a3f-986d-0af6cc11d1b9',
]);

const resolvePoisonSchedule = (
  value: PoisonSchedule | number | null | undefined
): PoisonSchedule => {
  if (value === 1 || value === 2 || value === 3 || value === 4 || value === 5)
    return value;
  return DEFAULT_POISON_SCHEDULE;
};

const buildCo2eFields = (selection: StepSelection) => ({
  co2eScope1: selection.co2eScope1 ?? DEFAULT_CO2E_FIELDS.co2eScope1,
  co2eScope2: selection.co2eScope2 ?? DEFAULT_CO2E_FIELDS.co2eScope2,
  co2eScope3: selection.co2eScope3 ?? DEFAULT_CO2E_FIELDS.co2eScope3,
  co2eUnit: selection.co2eUnit ?? DEFAULT_CO2E_FIELDS.co2eUnit,
});

const periodTypeToLabel = (t?: number | null) => {
  switch (t) {
    case 1:
      return 'Withholding Grazing';
    case 2:
      return 'Withholding Cutting';
    case 3:
      return 'Withholding Harvest';
    case 4:
      return 'Withholding Plantback';
    default:
      return 'Withholding';
  }
};

const buildWithholdPeriods = (selection: StepSelection) => {
  const periods = WITHHOLD_PERIOD_LABELS.map(({ valueKey, item }) => ({
    item,
    globalId: null,
    period: selection[valueKey],
  }))
    .filter((entry) => entry.period !== null && entry.period !== undefined)
    .map((entry) => ({
      item: entry.item,
      globalId: entry.globalId,
      period: entry.period ?? null,
    }));

  const specificPeriods = (selection.specWithholdPeriods ?? [])
    .filter((e) => e?.period !== null && e?.period !== undefined)
    .map((e) => {
      const base = periodTypeToLabel(e.periodType);
      const cropSuffix = e.snapCropName ? ` (${e.snapCropName})` : '';
      return {
        item: `${base}${cropSuffix}`,
        globalId: e.cropId ?? null,
        period: e.period ?? null,
      };
    });

  const unique = new Map<
    string,
    { item: string; globalId: string | null; period: number | null }
  >();
  [...periods, ...specificPeriods].forEach((entry) => {
    const key = `${entry.item}|${entry.globalId ?? ''}|${entry.period ?? ''}`;
    if (!unique.has(key)) {
      unique.set(key, entry);
    }
  });

  return Array.from(unique.values());
};

const buildPlantProtectionFields = (selection: StepSelection) => ({
  productGroups: selection.productGroups ?? ([] as IGcProductGroup[]),
  activeConstituents:
    selection.activeConstituents ?? ([] as IActiveConstituent[]),
  formulationCode: selection.formulationCode ?? null,
  formulation: selection.formulation ?? null,
  apvmaNumber: selection.apvmaNumber ?? null,
  poisonSchedule: resolvePoisonSchedule(selection.poisonSchedule),
  withholdPeriods: buildWithholdPeriods(selection),
  reentry: selection.reentry ?? null,
  reentryUnit: selection.reentryUnit ?? null,
  reentryNotes: selection.reentryNotes ?? null,
  batchNumber: null,
});

// TODO: rateUnitGroup is a dummy placeholder field on StepSelection (flagged in OP-1891 review).
// When the real G/M pickers are implemented for SoilInput/Biostimulant (and all other step
// types), rateUnitGroup should be removed. Each step type has a fixed GUnit category for its
// appUnit dropdown — wire to GUnit filtered by the correct category per step type:
//
//   Step type        | GUnit parameter                       | category
//   -----------------|---------------------------------------|----------
//   Plant Protection | Plant Protection - Small Solid/Liquid | 2
//   Adjuvant         | Plant Protection - Small Solid/Liquid | 2
//   Seed Dress       | Plant Protection - Small Solid/Liquid | 2
//   Soil Input       | Soil Input - Small Solid/Liquid       | 4
//   Biostimulant     | Soil Input - Small Solid/Liquid       | 4
//   Seed             | Soil Input - Large Solid              | 4
//   Operation        | Cost per area ($/ha) — read-only, hardcoded in UI | 8
//
// isLiquid on InputStepSoilInput is a derived field (not copied from G): true if the selected
// appUnit belongs to "Soil Input - Small Liquid" parameter, false otherwise.
//
// Ref: GUnit collection Confluence doc, OP-1891 description and review comment.
//
// Rate Unit dropdown (inline editing, OP-2277/OP-2278) reuses the same
// mapping above. "Small Solid/Liquid" in the table is two separate
// GUnit.snapParameter values (Solid OR Liquid, matched via `parameters`),
// not one combined string — confirmed against the mobile app's
// UnitParameterType/useGUnits, which also AND-matches GUnit.category
// against the step type's category bitmask, so both checks are applied here.
// Seed is intentionally omitted: on mobile its unit is fixed via the crop
// picker rather than a dropdown, and Seed steps aren't reachable in this web
// UI yet (Crop Establishment tasks are disabled) — revisit when that ships.
export const STEP_UNIT_FILTERS: Partial<Record<InputStepType, GUnitFilter>> = {
  [InputStepType.CropProtection]: {
    parameters: [
      UnitParameterType.PlantProtectionSmallSolid,
      UnitParameterType.PlantProtectionSmallLiquid,
    ],
    category: UnitCategory.PlantProtection,
  },
  [InputStepType.Adjuvant]: {
    parameters: [
      UnitParameterType.PlantProtectionSmallSolid,
      UnitParameterType.PlantProtectionSmallLiquid,
    ],
    category: UnitCategory.PlantProtection,
  },
  [InputStepType.SeedDress]: {
    parameters: [
      UnitParameterType.PlantProtectionSmallSolid,
      UnitParameterType.PlantProtectionSmallLiquid,
    ],
    category: UnitCategory.PlantProtection,
  },
  [InputStepType.SoilInput]: {
    parameters: [
      UnitParameterType.SoilInputSmallSolid,
      UnitParameterType.SoilInputSmallLiquid,
    ],
    category: UnitCategory.SoilInput,
  },
  [InputStepType.Biostimulant]: {
    parameters: [
      UnitParameterType.SoilInputSmallSolid,
      UnitParameterType.SoilInputSmallLiquid,
    ],
    category: UnitCategory.SoilInput,
  },
};

const deriveSoilInputIsLiquid = (selection: StepSelection): boolean => {
  const group = (selection.rateUnitGroup ?? '').toLowerCase();
  if (group) return group.includes('soil input - small liquid');

  const id = selection.unit?.globalId ?? '';
  if (id) return SOIL_INPUT_SMALL_LIQUID_UNIT_IDS.has(id);

  return false;
};

const buildSoilInputFields = (selection: StepSelection) => ({
  ...buildRateCostFields(selection),
  techStackId: selection.techStackId ?? null,
  snapTechStack: selection.snapTechStack ?? null,
  productGroups: selection.productGroups ?? ([] as IGcProductGroup[]),
  nutrientN: selection.nutrientN ?? null,
  nutrientP: selection.nutrientP ?? null,
  nutrientK: selection.nutrientK ?? null,
  nutrientS: selection.nutrientS ?? null,
  nutrientCa: selection.nutrientCa ?? null,
  nutrientMg: selection.nutrientMg ?? null,
  nutrientNa: selection.nutrientNa ?? null,
  nutrientB: selection.nutrientB ?? null,
  nutrientCu: selection.nutrientCu ?? null,
  nutrientFe: selection.nutrientFe ?? null,
  nutrientMn: selection.nutrientMn ?? null,
  nutrientZn: selection.nutrientZn ?? null,
  nutrientMo: selection.nutrientMo ?? null,
  nutrientCl: selection.nutrientCl ?? null,
  nutrientSi: selection.nutrientSi ?? null,
  nutrientNi: selection.nutrientNi ?? null,
  nutrientCo: selection.nutrientCo ?? null,
  additive: selection.additive ?? null,
  isSeedDressing: selection.isSeedDressing ?? false,
  ...buildCo2eFields(selection),
  ...buildManufacturerSupplierFields(selection),
  isLiquid: deriveSoilInputIsLiquid(selection),
  bulkDensity: selection.bulkDensity ?? null,
  bulkDensityUnit: selection.bulkDensityUnit ?? null,
});

const buildBiostimulantFields = (selection: StepSelection) => ({
  ...buildRateCostFields(selection),
  linkedStepId: null,
  actionModeId: selection.actionModeId ?? null,
  snapActionMode: selection.snapActionMode ?? null,
  productGroups: selection.productGroups ?? ([] as IGcProductGroup[]),
  ...buildManufacturerSupplierFields(selection),
});

/** The Input document's crop/variety, its matching merged GSeedSpecies and the Production unit, used to seed new production steps. */
export interface ProductionCropContext {
  crop: ICrop | null;
  cropOption: MergedCrop | null;
  /** Production "t" GUnit copied to yieldUnit/priceUnit; null until GUnits load. */
  tonneUnit: IUnit | null;
  /** True while the crop or the units a new production step copies are loading. */
  isResolving: boolean;
}

/** Crop/variety values for a new production step; all null when no context is given. */
const buildProductionCropFields = (
  context?: ProductionCropContext
): IInputStepProductionCropFields => {
  const crop = context?.crop ?? null;
  const cropOption =
    crop &&
    context?.cropOption &&
    matchesByIdOrName(
      { id: context.cropOption.globalId, name: context.cropOption.name },
      { id: crop.globalId, name: crop.cropName }
    )
      ? context.cropOption
      : null;
  const cropVariety = crop?.variety ?? null;
  const variety =
    cropVariety && cropOption
      ? (cropOption.varieties.find((v) =>
          matchesByIdOrName(
            { id: v.varietyId, name: v.varietyName },
            { id: cropVariety.globalId, name: cropVariety.varietyName }
          )
        ) ?? null)
      : null;

  return {
    cropId: crop?.globalId ?? null,
    snapCropName: crop?.cropName || null,
    varietyId: cropVariety?.globalId ?? null,
    snapVarietyName: cropVariety?.varietyName || null,
    techStackId: variety?.techStackId ?? null,
    snapTechStack: variety?.snapTechStack ?? null,
    ...buildManufacturerSupplierFields(variety),
    ...pickSeedSpeciesProductionFields(cropOption),
  };
};

export const buildStepFromSelection = (
  task: IInputTask,
  selection: StepSelection,
  userProfile?: PartialUserProfile,
  productionContext?: ProductionCropContext
): IInputStep => {
  const base = buildStepBase(task, selection, userProfile);

  switch (selection.stepType) {
    case InputStepType.Generic:
      return {
        ...base,
        stepType: InputStepType.Generic,
      };
    case InputStepType.Application:
      return {
        ...base,
        stepType: InputStepType.Application,
        ...buildRateCostFields(selection),
        ...buildCo2eFields(selection),
      };
    case InputStepType.CropProtection:
      return {
        ...base,
        stepType: InputStepType.CropProtection,
        ...buildRateCostFields(selection),
        ...buildPlantProtectionFields(selection),
        ...buildManufacturerSupplierFields(selection),
      };
    case InputStepType.Adjuvant:
      return {
        ...base,
        stepType: InputStepType.Adjuvant,
        ...buildRateCostFields(selection),
        ...buildPlantProtectionFields(selection),
        ...buildManufacturerSupplierFields(selection),
      };
    case InputStepType.SoilInput:
      return {
        ...base,
        stepType: InputStepType.SoilInput,
        ...buildSoilInputFields(selection),
      };
    case InputStepType.Biostimulant:
      return {
        ...base,
        stepType: InputStepType.Biostimulant,
        ...buildBiostimulantFields(selection),
      };
    case InputStepType.Production:
      return {
        ...base,
        stepType: InputStepType.Production,
        targetGrade: selection.targetGrade ?? '',
        yieldLow: null,
        yield: null,
        yieldHigh: null,
        retain: null,
        yieldUnit: productionContext?.tonneUnit ?? selection.unit ?? null,
        priceLow: null,
        price: null,
        priceHigh: null,
        priceUnit: productionContext?.tonneUnit ?? null,
        ...buildProductionCropFields(productionContext),
      };
    case InputStepType.Seed:
      return {
        ...base,
        stepType: InputStepType.Seed,
        ...buildRateCostFields(selection),
        item2: selection.item2 ?? null,
        globalId2: selection.globalId2 ?? null,
        techStackId: selection.techStackId ?? null,
        snapTechStack: selection.snapTechStack ?? null,
        ...buildManufacturerSupplierFields(selection),
      };
    case InputStepType.SeedDress:
      return {
        ...base,
        stepType: InputStepType.SeedDress,
        ...buildRateCostFields(selection),
        ...buildPlantProtectionFields(selection),
        ...buildManufacturerSupplierFields(selection),
        linkedStepId: selection.linkedSeedStepId ?? null,
        rateMode: selection.seedDressRateMode ?? SeedDressRateMode.Per100Kg,
      };
    default:
      throw new Error('Unsupported step type');
  }
};

/** The six Low/Medium/High yield and price fields edited in the Production matrix. */
export type ProductionMatrixKey = keyof Pick<
  IInputStepProduction,
  'yieldLow' | 'yield' | 'yieldHigh' | 'priceLow' | 'price' | 'priceHigh'
>;

const PRODUCTION_MATRIX_KEYS: ProductionMatrixKey[] = [
  'yieldLow',
  'yield',
  'yieldHigh',
  'priceLow',
  'price',
  'priceHigh',
];

const PRODUCTION_CROP_FIELD_KEYS = Object.keys(
  buildProductionCropFields()
) as (keyof IInputStepProductionCropFields)[];

const pickProductionFields = <K extends keyof IInputStepProduction>(
  step: IInputStepProduction,
  keys: K[]
): Pick<IInputStepProduction, K> =>
  Object.fromEntries(keys.map((key) => [key, step[key]])) as Pick<
    IInputStepProduction,
    K
  >;

/**
 * Applies a production step's new item while keeping what belongs to the step
 * rather than the item: the yield/price matrix, the crop snapshot taken when
 * the step was created, and its units (unless the replaced step had none).
 */
export const keepProductionStepValues = (
  previous: IInputStep,
  next: IInputStep
): IInputStep => {
  if (
    previous.stepType !== InputStepType.Production ||
    next.stepType !== InputStepType.Production
  ) {
    return next;
  }
  return {
    ...next,
    ...pickProductionFields(previous, PRODUCTION_MATRIX_KEYS),
    ...pickProductionFields(previous, PRODUCTION_CROP_FIELD_KEYS),
    yieldUnit: previous.yieldUnit ?? next.yieldUnit,
    priceUnit: previous.priceUnit ?? next.priceUnit,
  };
};
